import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import panelService from "../../services/panelService";

const initialState = {
    customerAccounts: null,
    customerAccountsLoading: false,
    departmentUserDeleteLoading: false,
    getDemoRequests: null,
    loading: false,
    assetCount: null,
    fraudUsageCount: null,
    dashboardReport: null,
    reportLoading: false,
    reportError: null,
    reportSchedule: null,
    reportScheduleLoading: false,
    reportScheduleSaving: false,
    osintUsageCount: null,
    emailLeakageUsageCount: null,
    socialSearcherUsageCount: null,
    scanPermissions: null,
    takeDownRequests: null,
    easmFormResults: null,
    allAccountSettings: null,
    allAccountSettingsLoading: false,
    takeDownDefaultRequest: {
        data: {
            "accountName": "",
            "status": null
        },
        params: {
            page: 0,
            size: 15
        }
    },
    successMessage: null,
    errorMessage: null,
    ticketList: null,
    userLoginHistoryList: null,
    moduleParameters: null,
    moduleParametersDefaultRequest: {
        data: {
            type: '',
            value: '',
            description: ''
        },
        params: {
            page: 0,
            size: 15,
            createdDate: "ASC"
        }
    },
    queueList: null
};

export const getCustomerAccounts = createAsyncThunk("panel/getCustomerAccounts", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getCustomerAccounts(data);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getAllAccountSettings = createAsyncThunk(
    "panel/getAllAccountSettings",
    async (_, { rejectWithValue }) => {
        try {
            return await panelService.getAllAccountSettings();
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);
export const updateAccountLicense = createAsyncThunk(
    "panel/updateAccountLicense",
    async (data, { rejectWithValue }) => {
        try {
            const response = await panelService.updateAccountLicense(
                data.accountId,
                {
                    expirationDate: data.expirationDate
                }
            );
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const updatePasswordDisplaySetting = createAsyncThunk(
    "panel/updatePasswordDisplaySetting",
    async (data, { rejectWithValue }) => {
        try {

            const response = await panelService.updatePasswordDisplaySetting(
                data.accountId,
                data.showEmailLeakagePasswords
            );

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const deleteUserInDepartment = createAsyncThunk("panel/deleteUserInDepartment", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.deleteUserInDepartment(data);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const softDeleteAccount = createAsyncThunk("panel/softDeleteAccount", async (data, {
    rejectWithValue, dispatch
}) => {
    try {
        const response = await panelService.softDeleteAccount(data);
        if (response?.status === 200) {
            dispatch(getCustomerAccounts());
        }
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getDemoRequests = createAsyncThunk("panel/getDemoRequests", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getDemoRequests();
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getAssetCount = createAsyncThunk("panel/getAssetCount", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getAssetCount();
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getFraudUsageCount = createAsyncThunk("panel/getFraudUsageCount", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getFraudUsageCount();
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getDashboardReport = createAsyncThunk("panel/getDashboardReport", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getDashboardReport();
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getReportSchedule = createAsyncThunk("panel/getReportSchedule", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getReportSchedule();
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const saveReportSchedule = createAsyncThunk("panel/saveReportSchedule", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.saveReportSchedule(data);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getOsintUsageCount = createAsyncThunk("panel/getOsintUsageCount", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getOsintUsageCount();
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getEmailLeakageUsageCount = createAsyncThunk("panel/getEmailLeakageUsageCount", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getEmailLeakageUsageCount();
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getSocialSearcherUsageCount = createAsyncThunk("panel/getSocialSearcherUsageCount", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getSocialSearcherUsageCount();
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getScanPermissions = createAsyncThunk("panel/getScanPermissions", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getScanPermissions();
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const updateScanPermissions = createAsyncThunk("panel/updateScanPermissions", async (data, {
    rejectWithValue, dispatch
}) => {
    try {
        const response = await panelService.updateScanPermissions(data?.data, data?.id);
        response && dispatch(getScanPermissions());
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getTakeDownRequests = createAsyncThunk("panel/getTakeDownRequests", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getTakeDownRequests(data);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const updateTakeDownStatus = createAsyncThunk("panel/updateTakeDownStatus", async (data, {
    rejectWithValue, dispatch, getState
}) => {
    try {
        const state = getState();
        const response = await panelService.updateTakeDownStatus(data?.params, data?.id);
        response && dispatch(getTakeDownRequests(state.panel.takeDownDefaultRequest));
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const enableUserSchedule = createAsyncThunk("panel/enableUserSchedule", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.enableUserSchedule(data);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const disableUserSchedule = createAsyncThunk("panel/disableUserSchedule", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.disableUserSchedule(data);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getTickets = createAsyncThunk("panel/getTickets", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getTickets(data);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getUserLoginHistory = createAsyncThunk("panel/getUserLoginHistory", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getUserLoginHistory(data);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getModuleParameters = createAsyncThunk("panel/getModuleParameters", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getModuleParameters(data);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const createModuleParameter = createAsyncThunk("panel/createModuleParameter", async (data, {
    rejectWithValue, dispatch, getState
}) => {
    try {
        const state = getState();
        const response = await panelService.createModuleParameter(data);
        if (response?.status === 200) {
            dispatch(getModuleParameters(state?.panel?.takeDownDefaultRequest));
        }
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const updateModuleParameter = createAsyncThunk("panel/updateModuleParameter", async (data, {
    rejectWithValue, dispatch, getState
}) => {
    try {
        const state = getState();
        const response = await panelService.updateModuleParameter(data?.data, data?.id);
        if (response?.status === 200) {
            dispatch(getModuleParameters(state?.panel?.takeDownDefaultRequest));
        }
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getQueueList = createAsyncThunk("panel/getQueueList", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getQueueList();
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const deleteQueue = createAsyncThunk("panel/deleteQueue", async (data, {
    rejectWithValue, dispatch
}) => {
    try {
        const response = await panelService.deleteQueue(data);
        if (response?.status === 200) {
            dispatch(getQueueList());
        }
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const clearCache = createAsyncThunk("panel/clearCache", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.clearCache();
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getEasmFormResults = createAsyncThunk("panel/getEasmFormResults", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.getEasmFormResults(data);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const syncDataWells = createAsyncThunk("panel/syncDataWells", async (data, {
    rejectWithValue
}) => {
    try {
        return await panelService.syncDataWells();
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const panelSlice = createSlice({
    name: 'panelSlice',
    initialState,
    reducers: {
        setTakeDownDefaultRequest(state, action) {
            console.log(action.payload);
            state.takeDownDefaultRequest = action.payload
        },
        resetMessages(state) {
            state.successMessage = null;
            state.errorMessage = null;
        }
    },
    extraReducers: (builder) => {
        //GET CUSTOMER ACCOUNTS
        builder.addCase(getCustomerAccounts.fulfilled, (state, action) => {
            state.customerAccountsLoading = false;
            state.customerAccounts = action.payload.data?.accounts;
        });

        builder.addCase(getCustomerAccounts.pending, (state, action) => {
            state.customerAccountsLoading = true;
        });
        builder.addCase(getCustomerAccounts.rejected, (state, action) => {
            state.customerAccountsLoading = false;
        });
        //DELETE USER IN DEPARTMENT
        builder.addCase(deleteUserInDepartment.fulfilled, (state, action) => {
            state.departmentUserDeleteLoading = false;
        });
        builder.addCase(deleteUserInDepartment.pending, (state, action) => {
            state.departmentUserDeleteLoading = true;
        });
        builder.addCase(deleteUserInDepartment.rejected, (state, action) => {
            state.departmentUserDeleteLoading = false;
        });

        builder.addCase(softDeleteAccount.fulfilled, (state, action) => {
            state.successMessage = 'success';
            state.loading = false;
        });
        builder.addCase(softDeleteAccount.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(softDeleteAccount.rejected, (state, action) => {
            state.errorMessage = 'error';
            state.loading = false;
        });
        //GET DEMO REQUESTS
        builder.addCase(getDemoRequests.fulfilled, (state, action) => {
            state.demoRequests = action.payload.data;
            state.loading = false;
        });
        builder.addCase(getDemoRequests.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(getDemoRequests.rejected, (state, action) => {
            state.loading = false;
        });
        //GET ASSET COUNT
        builder.addCase(getAssetCount.fulfilled, (state, action) => {
            state.assetCount = action.payload.data;
            state.loading = false;
        });
        builder.addCase(getAssetCount.pending, (state, action) => {
            state.assetCount = null;
            state.loading = true;
        });
        builder.addCase(getAssetCount.rejected, (state, action) => {
            state.loading = false;
        });
        //GET FRAUD USAGE COUNT
        builder.addCase(getFraudUsageCount.fulfilled, (state, action) => {
            state.fraudUsageCount = action.payload.data;
            state.loading = false;
        });
        builder.addCase(getFraudUsageCount.pending, (state, action) => {
            state.fraudUsageCount = null;
            state.loading = true;
        });
        builder.addCase(getFraudUsageCount.rejected, (state, action) => {
            state.loading = false;
        });
        //GET DASHBOARD REPORT — uses its own reportLoading flag (doesn't touch
        //the shared loading; otherwise other panel thunks would stomp the spinner)
        builder.addCase(getDashboardReport.pending, (state, action) => {
            state.reportLoading = true;
            state.reportError = null;
        });
        builder.addCase(getDashboardReport.fulfilled, (state, action) => {
            state.dashboardReport = action.payload.data;
            state.reportLoading = false;
        });
        builder.addCase(getDashboardReport.rejected, (state, action) => {
            state.reportLoading = false;
            state.reportError = action.payload || true;
        });
        //GET REPORT SCHEDULE
        builder.addCase(getReportSchedule.pending, (state) => {
            state.reportSchedule = null;
            state.reportScheduleLoading = true;
        });
        builder.addCase(getReportSchedule.fulfilled, (state, action) => {
            state.reportSchedule = action.payload.data;
            state.reportScheduleLoading = false;
        });
        builder.addCase(getReportSchedule.rejected, (state) => {
            state.reportScheduleLoading = false;
        });
        builder.addCase(saveReportSchedule.pending, (state) => {
            state.reportScheduleSaving = true;
        });
        builder.addCase(saveReportSchedule.fulfilled, (state, action) => {
            state.reportSchedule = action.payload.data;
            state.reportScheduleSaving = false;
        });
        builder.addCase(saveReportSchedule.rejected, (state) => {
            state.reportScheduleSaving = false;
        });
        //GET OSINT USAGE COUNT
        builder.addCase(getOsintUsageCount.fulfilled, (state, action) => {
            state.osintUsageCount = action.payload.data;
            state.loading = false;
        });
        builder.addCase(getOsintUsageCount.pending, (state, action) => {
            state.osintUsageCount = null;
            state.loading = true;
        });
        builder.addCase(getOsintUsageCount.rejected, (state, action) => {
            state.loading = false;
        });
        //GET EMAIL LEAKAGE USAGE COUNT
        builder.addCase(getEmailLeakageUsageCount.fulfilled, (state, action) => {
            state.emailLeakageUsageCount = action.payload.data;
            state.loading = false;
        });
        builder.addCase(getEmailLeakageUsageCount.pending, (state, action) => {
            state.emailLeakageUsageCount = null;
            state.loading = true;
        });
        builder.addCase(getEmailLeakageUsageCount.rejected, (state, action) => {
            state.loading = false;
        });
        //GET SOCIAL SEARCHER USAGE COUNT
        builder.addCase(getSocialSearcherUsageCount.fulfilled, (state, action) => {
            state.socialSearcherUsageCount = action.payload.data;
            state.loading = false;
        });
        builder.addCase(getSocialSearcherUsageCount.pending, (state, action) => {
            state.socialSearcherUsageCount = null;
            state.loading = true;
        });
        builder.addCase(getSocialSearcherUsageCount.rejected, (state, action) => {
            state.loading = false;
        });
        //GET SCAN PERMISSIONS
        builder.addCase(getScanPermissions.fulfilled, (state, action) => {
            state.scanPermissions = action.payload.data;
            state.loading = false;
        });
        builder.addCase(getScanPermissions.pending, (state, action) => {
            state.scanPermissions = null;
            state.loading = true;
        });
        builder.addCase(getScanPermissions.rejected, (state, action) => {
            state.loading = false;
        });
        //UPDATE SCAN PERMISSIONS
        builder.addCase(updateScanPermissions.fulfilled, (state, action) => {
            state.loading = false;
        });
        builder.addCase(updateScanPermissions.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(updateScanPermissions.rejected, (state, action) => {
            state.loading = false;
        });
        //GET TAKE DOWN REQUESTS
        builder.addCase(getTakeDownRequests.fulfilled, (state, action) => {
            state.takeDownRequests = action.payload.data;
            state.loading = false;
        });
        builder.addCase(getTakeDownRequests.pending, (state, action) => {
            state.takeDownRequests = null;
            state.loading = true;
        });
        builder.addCase(getTakeDownRequests.rejected, (state, action) => {
            state.loading = false;
        });
        //UPDATE TAKE DOWN STATUS
        builder.addCase(updateTakeDownStatus.fulfilled, (state, action) => {
            state.loading = false;
        });
        builder.addCase(updateTakeDownStatus.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(updateTakeDownStatus.rejected, (state, action) => {
            state.loading = false;
        });
        //START USER SCHEDULE
        builder.addCase(enableUserSchedule.fulfilled, (state, action) => {
            if (action.payload?.status === 200) {
                state.successMessage = 'success';
            } else {
                state.errorMessage = null;
            }
            state.loading = false;
        });
        builder.addCase(enableUserSchedule.pending, (state, action) => {
            state.loading = true;
        });

        builder.addCase(enableUserSchedule.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage = 'error';
        });
        //STOP USER SCHEDULE
        builder.addCase(disableUserSchedule.fulfilled, (state, action) => {
            if (action.payload?.status === 200) {
                state.successMessage = 'success';
            } else {
                state.errorMessage = null;
            }
            state.loading = false;
        });
        builder.addCase(disableUserSchedule.pending, (state, action) => {
            state.loading = true;
        });

        builder.addCase(disableUserSchedule.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage = 'error';
        });
        //GET TICKETS
        builder.addCase(getTickets.fulfilled, (state, action) => {
            state.loading = false;
            state.ticketList = action.payload.data;
        });
        builder.addCase(getTickets.pending, (state, action) => {
            state.loading = true;
            state.ticketList = null;
        });
        builder.addCase(getTickets.rejected, (state, action) => {
            state.loading = false;
        });
        //GET USER LOGIN HISTORY
        builder.addCase(getUserLoginHistory.fulfilled, (state, action) => {
            state.loading = false;
            state.userLoginHistory = action.payload.data;
        });
        builder.addCase(getUserLoginHistory.pending, (state, action) => {
            state.loading = true;
            state.userLoginHistory = null;
        });
        builder.addCase(getUserLoginHistory.rejected, (state, action) => {
            state.loading = false;
        });
        //GET MODULE PARAMETERS
        builder.addCase(getModuleParameters.fulfilled, (state, action) => {
            state.loading = false;
            state.moduleParameters = action.payload.data;
        });
        builder.addCase(getModuleParameters.pending, (state, action) => {
            state.loading = true;
            state.moduleParameters = null;
        });
        builder.addCase(getModuleParameters.rejected, (state, action) => {
            state.loading = false;
        });
        //CREATE MODULE PARAMETER
        builder.addCase(createModuleParameter.fulfilled, (state, action) => {
            state.loading = false;
            state.successMessage = 'success';
        });
        builder.addCase(createModuleParameter.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(createModuleParameter.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage = 'error';
        });
        //UPDATE MODULE PARAMETER
        builder.addCase(updateModuleParameter.fulfilled, (state, action) => {
            state.loading = false;
            state.successMessage = 'success';
        });
        builder.addCase(updateModuleParameter.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(updateModuleParameter.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage = 'error';
        });
        //GET QUEUE LIST
        builder.addCase(getQueueList.fulfilled, (state, action) => {
            state.queueList = action.payload.data;
            state.loading = false;
        });
        builder.addCase(getQueueList.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(getQueueList.rejected, (state, action) => {
            state.loading = false;
        });
        //DELETE QUEUE
        builder.addCase(deleteQueue.fulfilled, (state, action) => {
            state.successMessage = 'success';
            state.loading = false;
        });
        builder.addCase(deleteQueue.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(deleteQueue.rejected, (state, action) => {
            state.errorMessage = 'error';
            state.loading = false;
        });
        //DELETE CACHE
        builder.addCase(clearCache.fulfilled, (state, action) => {
            state.successMessage = 'success';
            state.loading = false;
        });
        builder.addCase(clearCache.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(clearCache.rejected, (state, action) => {
            state.errorMessage = 'error';
            state.loading = false;
        });
        //GET EASM FORM RESULTS
        builder.addCase(getEasmFormResults.fulfilled, (state, action) => {
            state.easmFormResults = action.payload.data;
            state.loading = false;
        });
        builder.addCase(getEasmFormResults.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(getEasmFormResults.rejected, (state, action) => {
            state.loading = false;
        });
        //SYNC DATA WELLS
        builder.addCase(syncDataWells.fulfilled, (state, action) => {
            state.successMessage = 'success';
            state.loading = false;
        });
        builder.addCase(syncDataWells.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(syncDataWells.rejected, (state, action) => {
            state.errorMessage = 'error';
            state.loading = false;
        });

        //ACCOUNT SETTINGS
        builder.addCase(updateAccountLicense.fulfilled, (state, action) => {
            state.loading = false;
            state.successMessage = 'License updated successfully';
        });
        builder.addCase(updateAccountLicense.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(updateAccountLicense.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage = 'Failed to update license';
        });

        builder.addCase(updatePasswordDisplaySetting.fulfilled, (state, action) => {
            state.loading = false;
            state.successMessage = 'success';
        });

        builder.addCase(updatePasswordDisplaySetting.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(updatePasswordDisplaySetting.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage = 'error';
        });
        builder.addCase(getAllAccountSettings.fulfilled, (state, action) => {
            state.allAccountSettings = action.payload.data;
            state.allAccountSettingsLoading = false;
        });

        builder.addCase(getAllAccountSettings.pending, (state, action) => {
            state.allAccountSettings = null;
            state.allAccountSettingsLoading = true;
        });

        builder.addCase(getAllAccountSettings.rejected, (state, action) => {
            state.allAccountSettingsLoading = false;
        });
    }
});

export const {
    setTakeDownDefaultRequest,
    resetMessages
} = panelSlice.actions;

export default panelSlice.reducer;